import { queryOptions } from '@tanstack/react-query';

import {
    CREDIT_USE_STATE,
    type CustomerXero,
    type OrderXero,
    XERO_DOCUMENT_STATE,
    XERO_STATE,
    type XeroDocumentState,
    xeroApi,
} from '@/features/xero/api';

export const xeroKeys = {
    connection: ['xero', 'connection'] as const,
    check: ['xero', 'check'] as const,
    /** Under the order's own keys: what changes an order's documents is a write to the order. */
    order: (orderId: number) => ['orders', 'xero', orderId] as const,
    accounts: ['xero', 'accounts'] as const,
    /** Under the client's own keys: a top-up raises its credit note (ALS-384). */
    customer: (customerId: number) => ['customers', 'xero', customerId] as const,
    /**
     * Under the order's Xero keys, so whatever marks those stale marks the link stale too, and keyed
     * on where the invoice stands and when its payments were last read, so the link is asked for
     * again once the invoice reaches Xero and after a payment read back from it (ALS-70).
     */
    paymentLink: (orderId: number, invoiceState: XeroDocumentState | null, readOn: string | null) =>
        [
            ...xeroKeys.order(orderId),
            'payment-link',
            invoiceState ?? 'none',
            readOn ?? 'unread',
        ] as const,
};

/** The Xero accounts register (ALS-386). */
export const xeroAccountsQuery = queryOptions({
    queryKey: xeroKeys.accounts,
    queryFn: xeroApi.accounts,
});

/**
 * While the connection reads as reconnecting the API asks Xero again on every read, so the page
 * keeps reading until it settles one way or the other; otherwise once a minute is plenty.
 */
const RECONNECTING_POLL_MS = 10_000;

export const xeroConnectionQuery = queryOptions({
    queryKey: xeroKeys.connection,
    queryFn: xeroApi.connection,
    staleTime: 60_000,
    refetchInterval: (query) =>
        query.state.data?.state === XERO_STATE.syncing ? RECONNECTING_POLL_MS : false,
});

/** How often an order page asks again while one of its documents is on its way to Xero. */
const SENDING_POLL_MS = 10_000;

/**
 * True while any of the order's documents, any credit applied to it (ALS-384) or any payment
 * recorded on it (ALS-456) is on its way, which the queue settles within a minute.
 */
export function anySending(order: OrderXero | undefined): boolean {
    if (!order) return false;
    return (
        [order.invoice, ...order.creditNotes, ...order.payments].some(
            (document) => document?.state === XERO_DOCUMENT_STATE.waiting,
        ) || order.credit.some((use) => use.state === CREDIT_USE_STATE.waiting)
    );
}

/** How often an order page asks again while its invoice is in Xero and money is owed (ALS-70). */
const WATCHING_POLL_MS = 60_000;

/**
 * When the order's page asks again where its documents stand: quickly while one is on its way, and
 * every minute while its invoice is in Xero and money is owed, so a payment Xero reads back shows
 * up without a reload (ALS-70). Otherwise not at all; a tab in the background never asks.
 */
export function orderXeroRefetch(order: OrderXero | undefined, owing: boolean): number | false {
    if (anySending(order)) return SENDING_POLL_MS;
    return owing && order?.invoice?.state === XERO_DOCUMENT_STATE.sent ? WATCHING_POLL_MS : false;
}

/** `owing`: the order still owes money, so a payment may yet arrive from Xero. */
export const orderXeroQuery = (orderId: number, owing = false) =>
    queryOptions({
        queryKey: xeroKeys.order(orderId),
        queryFn: () => xeroApi.orderDocuments(orderId),
        refetchInterval: (query) => orderXeroRefetch(query.state.data, owing),
    });

/** True while one of the client's top-up credit notes is on its way to Xero. */
function anyNoteSending(customer: CustomerXero | undefined): boolean {
    return (
        customer?.creditNotes.some((note) => note.document.state === XERO_DOCUMENT_STATE.waiting) ??
        false
    );
}

/** The client's wallet in Xero (ALS-384), asked again quickly while a top-up's credit note goes. */
export const customerXeroQuery = (customerId: number) =>
    queryOptions({
        queryKey: xeroKeys.customer(customerId),
        queryFn: () => xeroApi.customer(customerId),
        refetchInterval: (query) => (anyNoteSending(query.state.data) ? SENDING_POLL_MS : false),
    });

/**
 * An order's payment link (ALS-385). A minute's grace, as the connection has: the link itself
 * never changes, and whether there is one only changes with a payment or Xero's connection.
 */
export const orderPaymentLinkQuery = (
    orderId: number,
    invoiceState: XeroDocumentState | null,
    readOn: string | null,
) =>
    queryOptions({
        queryKey: xeroKeys.paymentLink(orderId, invoiceState, readOn),
        queryFn: () => xeroApi.paymentLink(orderId),
        staleTime: 60_000,
    });
